import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '../common/Dialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  fetchCrews, createCrew, updateCrew, deleteCrew
} from '../../store/slices/crewsSlice';
import {
  fetchTrades, createTrade, updateTrade, deleteTrade
} from '../../store/slices/tradesSlice';
import {
  fetchPeople, createPerson, updatePerson, deletePerson
} from '../../store/slices/peopleSlice';
import { fetchEvents } from '../../store/slices/eventsSlice';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6B7280'];
const tradeId = (t) => String(t?._id || t || '');
const personId = (p) => String(p?._id || p || '');

const Icon = ({ name, size = 16 }) => {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    edit: <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />,
    trash: <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
    inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
    tag: <><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

const CalendarManagerDialog = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const crews = useSelector((s) => s.crews.items);
  const trades = useSelector((s) => s.trades.items);
  const people = useSelector((s) => s.people.items);
  const role = useSelector((s) => s.auth.user?.profile?.role);
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState('crews');
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false, title: '', message: '', confirmText: 'Confirm', type: 'danger', onConfirm: null
  });

  useEffect(() => {
    if (isOpen) {
      setTab('crews');
      dispatch(fetchCrews());
      dispatch(fetchTrades());
      dispatch(fetchPeople());
      dispatch(fetchEvents());
    }
  }, [isOpen, dispatch]);

  const openConfirm = (cfg) => setConfirmConfig({ isOpen: true, ...cfg });
  const closeConfirm = () => setConfirmConfig((p) => ({ ...p, isOpen: false, onConfirm: null }));

  const headerTitle = isSuperAdmin ? 'Manage Crews, People & Trades' : 'Manage Crews & People';

  return (
    <>
      <Dialog isOpen={isOpen} onClose={onClose}>
        <div className="event-dialog">
          <div className="ed-header">
            <h2>{headerTitle}</h2>
            <button className="ed-close" onClick={onClose}>×</button>
          </div>

          <div className="ed-tabs">
            <button className={`ed-tab ${tab === 'crews' ? 'active' : ''}`} onClick={() => setTab('crews')}>Crews</button>
            <button className={`ed-tab ${tab === 'people' ? 'active' : ''}`} onClick={() => setTab('people')}>People</button>
            {isSuperAdmin && (
              <button className={`ed-tab ${tab === 'trades' ? 'active' : ''}`} onClick={() => setTab('trades')}>Trades</button>
            )}
          </div>

          <div className="ed-body">
            {tab === 'crews' && (
              <CrewsTab
                crews={crews}
                trades={trades}
                people={people}
                dispatch={dispatch}
                onRequestConfirm={openConfirm}
              />
            )}
            {tab === 'people' && (
              <PeopleTab
                people={people}
                trades={trades}
                dispatch={dispatch}
                onRequestConfirm={openConfirm}
              />
            )}
            {tab === 'trades' && isSuperAdmin && (
              <TradesTab
                trades={trades}
                dispatch={dispatch}
                onRequestConfirm={openConfirm}
              />
            )}
          </div>

          <div className="ed-footer">
            <div />
            <button className="cal-btn cal-btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={closeConfirm}
        onConfirm={() => {
          if (typeof confirmConfig.onConfirm === 'function') confirmConfig.onConfirm();
        }}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        type={confirmConfig.type}
      />
    </>
  );
};

const TradePicker = ({ trades, selectedIds, onChange }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return trades;
    return (trades || []).filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [trades, search]);

  const toggle = (id) => {
    const has = selectedIds.includes(id);
    onChange(has ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className="mgr-members-field">
      <input
        className="ed-input mgr-members-search"
        placeholder="Search trades…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mgr-member-list">
        {(trades || []).length === 0 && (
          <div className="mgr-members-empty">No trades yet. Add some in the Trades tab.</div>
        )}
        {(trades || []).length > 0 && filtered.length === 0 && (
          <div className="mgr-members-empty">No trades match "{search}"</div>
        )}
        {filtered.map((t) => {
          const id = tradeId(t);
          const checked = selectedIds.includes(id);
          return (
            <label key={id} className={`mgr-member-row ${checked ? 'is-checked' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(id)} />
              <span className="mgr-member-name">{t.name}</span>
            </label>
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <div className="mgr-members-count">
          {selectedIds.length} trade{selectedIds.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
};

const PeoplePicker = ({ people, selectedIds, onChange }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return (people || []).filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [people, search]);

  const toggle = (id) => {
    const has = selectedIds.includes(id);
    onChange(has ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className="mgr-members-field">
      <input
        className="ed-input mgr-members-search"
        placeholder="Search people…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mgr-member-list">
        {(people || []).length === 0 && (
          <div className="mgr-members-empty">No people yet. Add some in the People tab.</div>
        )}
        {(people || []).length > 0 && filtered.length === 0 && (
          <div className="mgr-members-empty">No people match "{search}"</div>
        )}
        {filtered.map((p) => {
          const id = personId(p);
          const checked = selectedIds.includes(id);
          return (
            <label key={id} className={`mgr-member-row ${checked ? 'is-checked' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(id)} />
              <span className="mgr-member-name">{p.name}</span>
            </label>
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <div className="mgr-members-count">
          {selectedIds.length} {selectedIds.length === 1 ? 'person' : 'people'} selected
        </div>
      )}
    </div>
  );
};

const ColorPicker = ({ value, onChange }) => (
  <div className="ed-colors">
    {COLORS.map((c) => (
      <button
        key={c}
        type="button"
        className={`ed-color ${value === c ? 'active' : ''}`}
        style={{ background: c, ['--accent']: c }}
        onClick={() => onChange(c)}
        aria-label={`Choose color ${c}`}
      />
    ))}
  </div>
);

const CrewsTab = ({ crews, trades, people, dispatch, onRequestConfirm }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[1]);
  const [selectedTradeIds, setSelectedTradeIds] = useState([]);
  const [selectedPeopleIds, setSelectedPeopleIds] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(COLORS[1]);
  const [editTrades, setEditTrades] = useState([]);
  const [editPeople, setEditPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  const resetAddForm = () => {
    setName('');
    setColor(COLORS[1]);
    setSelectedTradeIds([]);
    setSelectedPeopleIds([]);
  };

  const cancelAddForm = () => {
    resetAddForm();
    setShowAddForm(false);
  };

  const submit = async () => {
    if (!name.trim()) return;
    await dispatch(createCrew({
      name: name.trim(),
      color,
      trades: selectedTradeIds,
      people: selectedPeopleIds
    }));
    resetAddForm();
    setShowAddForm(false);
  };

  const startEditing = (crew) => {
    setEditingId(crew._id);
    setEditName(crew.name || '');
    setEditColor(crew.color || COLORS[1]);
    setEditTrades((crew.trades || []).map(tradeId));
    setEditPeople((crew.people || []).map(personId));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditColor(COLORS[1]);
    setEditTrades([]);
    setEditPeople([]);
  };

  const saveCrew = async (crew) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await dispatch(updateCrew({
        id: crew._id,
        updates: {
          name: editName.trim(),
          color: editColor,
          trades: editTrades,
          people: editPeople
        }
      }));
      cancelEditing();
    } finally {
      setSaving(false);
    }
  };

  const removePersonFromCrew = (crew, pid) => {
    const nextPeople = (crew.people || []).map(personId).filter((x) => x !== pid);
    dispatch(updateCrew({ id: crew._id, updates: { people: nextPeople } }));
  };

  return (
    <div className="mgmt-section">
      <div className="mgmt-toolbar">
        <div className="mgmt-toolbar-meta">
          {crews.length === 0
            ? 'No crews yet'
            : <><strong>{crews.length}</strong> {crews.length === 1 ? 'crew' : 'crews'}</>}
        </div>
        {!showAddForm && (
          <button className="cal-btn cal-btn-primary" onClick={() => setShowAddForm(true)}>
            <Icon name="plus" size={14} />
            <span>Add crew</span>
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="mgmt-add-card">
          <div className="mgmt-add-card-title">New crew</div>
          <div className="ed-row">
            <label>Crew name</label>
            <input
              className="ed-input"
              placeholder="e.g. Crew Alpha"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              autoFocus
            />
          </div>
          <div className="ed-row">
            <label>Color</label>
            <ColorPicker value={color} onChange={setColor} />
          </div>
          <div className="ed-row mgr-members-row">
            <label>Trades</label>
            <TradePicker trades={trades} selectedIds={selectedTradeIds} onChange={setSelectedTradeIds} />
          </div>
          <div className="ed-row mgr-members-row">
            <label>People</label>
            <PeoplePicker people={people} selectedIds={selectedPeopleIds} onChange={setSelectedPeopleIds} />
          </div>
          <div className="mgmt-add-card-actions">
            <button className="cal-btn" onClick={cancelAddForm}>Cancel</button>
            <button className="cal-btn cal-btn-primary" onClick={submit} disabled={!name.trim()}>
              Add crew
            </button>
          </div>
        </div>
      )}

      {crews.length === 0 ? (
        <div className="mgmt-empty">
          <div className="mgmt-empty-icon"><Icon name="inbox" size={16} /></div>
          <div className="mgmt-empty-text">No crews yet</div>
          <div className="mgmt-empty-sub">Crews represent the teams you schedule on the calendar. Add one above to get started.</div>
        </div>
      ) : (
        <div className="mgmt-list">
          {crews.map((c) => {
            const crewTrades = (c.trades || []).filter((t) => t && (t.name || t._id));
            const crewPeople = (c.people || []).filter((p) => p && (p.name || p._id));
            const isEditing = editingId === c._id;
            return (
              <div key={c._id} className={`mgmt-card ${isEditing ? 'is-editing' : ''}`}>
                {!isEditing ? (
                  <>
                    <div className="mgmt-card-row">
                      <span className="mgmt-card-swatch" style={{ background: c.color }} />
                      <span className="mgmt-card-name">{c.name}</span>
                      <div className="mgmt-card-actions">
                        <button
                          className="mgmt-icon-btn"
                          onClick={() => startEditing(c)}
                          title="Edit crew"
                          aria-label="Edit crew"
                        >
                          <Icon name="edit" size={14} />
                        </button>
                        <button
                          className="mgmt-icon-btn is-danger"
                          onClick={() => onRequestConfirm({
                            title: 'Delete Crew',
                            message: `Delete "${c.name}"? This action cannot be undone.`,
                            confirmText: 'Delete',
                            type: 'danger',
                            onConfirm: () => dispatch(deleteCrew(c._id))
                          })}
                          title="Delete crew"
                          aria-label="Delete crew"
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    </div>
                    {crewTrades.length > 0 ? (
                      <div className="mgmt-card-trades">
                        {crewTrades.map((t) => (
                          <span key={tradeId(t)} className="mgmt-trade-chip">{t.name || 'Trade'}</span>
                        ))}
                      </div>
                    ) : (
                      <div className="mgmt-card-empty-trades">No trades assigned</div>
                    )}
                    {crewPeople.length > 0 ? (
                      <div className="mgmt-card-people-list">
                        {crewPeople.map((p) => {
                          const pTrades = (p.trades || []).filter((t) => t && (t.name || t._id));
                          return (
                            <div key={personId(p)} className="mgmt-person-row">
                              <span className="mgmt-person-row-name">
                                <Icon name="user" size={12} />
                                <span>{p.name || 'Person'}</span>
                              </span>
                              {pTrades.length > 0 && (
                                <span className="mgmt-person-row-trades">
                                  {pTrades.map((t) => (
                                    <span key={tradeId(t)} className="mgmt-trade-chip mgmt-trade-chip-sm">
                                      {t.name || 'Trade'}
                                    </span>
                                  ))}
                                </span>
                              )}
                              <button
                                type="button"
                                className="mgmt-person-row-remove"
                                onClick={() => removePersonFromCrew(c, personId(p))}
                                title="Remove from crew"
                                aria-label={`Remove ${p.name || 'person'} from crew`}
                              >×</button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mgmt-card-empty-trades">No people assigned</div>
                    )}
                  </>
                ) : (
                  <div className="mgmt-edit-panel">
                    <div className="ed-row">
                      <label>Crew name</label>
                      <input
                        className="ed-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="ed-row">
                      <label>Color</label>
                      <ColorPicker value={editColor} onChange={setEditColor} />
                    </div>
                    <div className="ed-row mgr-members-row">
                      <label>Trades</label>
                      <TradePicker trades={trades} selectedIds={editTrades} onChange={setEditTrades} />
                    </div>
                    <div className="ed-row mgr-members-row">
                      <label>People</label>
                      <PeoplePicker people={people} selectedIds={editPeople} onChange={setEditPeople} />
                    </div>
                    <div className="mgmt-edit-actions">
                      <button className="cal-btn" onClick={cancelEditing} disabled={saving}>Cancel</button>
                      <button
                        className="cal-btn cal-btn-primary"
                        onClick={() => saveCrew(c)}
                        disabled={!editName.trim() || saving}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PeopleTab = ({ people, trades, dispatch, onRequestConfirm }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState('');
  const [selectedTradeIds, setSelectedTradeIds] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editTrades, setEditTrades] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const resetAddForm = () => {
    setName('');
    setSelectedTradeIds([]);
  };

  const submit = async () => {
    if (!name.trim()) return;
    await dispatch(createPerson({ name: name.trim(), trades: selectedTradeIds }));
    resetAddForm();
    setShowAddForm(false);
  };

  const startEditing = (person) => {
    setEditingId(person._id);
    setEditName(person.name || '');
    setEditTrades((person.trades || []).map(tradeId));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditTrades([]);
  };

  const savePerson = async (person) => {
    const next = editName.trim();
    if (!next) return;
    setSavingId(person._id);
    try {
      await dispatch(updatePerson({
        id: person._id,
        updates: { name: next, trades: editTrades }
      }));
      cancelEditing();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="mgmt-section">
      <div className="mgmt-toolbar">
        <div className="mgmt-toolbar-meta">
          {(people || []).length === 0
            ? 'No people yet'
            : <><strong>{people.length}</strong> {people.length === 1 ? 'person' : 'people'}</>}
        </div>
        {!showAddForm && (
          <button className="cal-btn cal-btn-primary" onClick={() => setShowAddForm(true)}>
            <Icon name="plus" size={14} />
            <span>Add person</span>
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="mgmt-add-card">
          <div className="mgmt-add-card-title">New person</div>
          <div className="ed-row">
            <label>Name</label>
            <input
              className="ed-input"
              placeholder="e.g. John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              autoFocus
            />
          </div>
          <div className="ed-row mgr-members-row">
            <label>Trades</label>
            <TradePicker trades={trades} selectedIds={selectedTradeIds} onChange={setSelectedTradeIds} />
          </div>
          <div className="mgmt-add-card-actions">
            <button className="cal-btn" onClick={() => { resetAddForm(); setShowAddForm(false); }}>Cancel</button>
            <button className="cal-btn cal-btn-primary" onClick={submit} disabled={!name.trim()}>
              Add person
            </button>
          </div>
        </div>
      )}

      {(people || []).length === 0 ? (
        <div className="mgmt-empty">
          <div className="mgmt-empty-icon"><Icon name="user" size={16} /></div>
          <div className="mgmt-empty-text">No people yet</div>
          <div className="mgmt-empty-sub">People are individuals you can attach to crews. They are independent of user accounts.</div>
        </div>
      ) : (
        <div className="mgmt-list">
          {(people || []).map((p) => {
            const isEditing = editingId === p._id;
            const isSaving = savingId === p._id;
            const personTrades = (p.trades || []).filter((t) => t && (t.name || t._id));
            return (
              <div key={p._id} className={`mgmt-card ${isEditing ? 'is-editing' : ''}`}>
                {!isEditing ? (
                  <>
                    <div className="mgmt-card-row">
                      <span className="mgmt-card-name" style={{ paddingLeft: 0 }}>
                        <Icon name="user" size={14} />
                        <span style={{ marginLeft: 8 }}>{p.name}</span>
                      </span>
                      <div className="mgmt-card-actions">
                        <button
                          className="mgmt-icon-btn"
                          onClick={() => startEditing(p)}
                          title="Edit person"
                          aria-label="Edit person"
                        >
                          <Icon name="edit" size={14} />
                        </button>
                        <button
                          className="mgmt-icon-btn is-danger"
                          onClick={() => onRequestConfirm({
                            title: 'Remove Person',
                            message: `Remove "${p.name}"? They will be unassigned from any crews.`,
                            confirmText: 'Remove',
                            type: 'danger',
                            onConfirm: () => dispatch(deletePerson(p._id))
                          })}
                          title="Remove person"
                          aria-label="Remove person"
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    </div>
                    {personTrades.length > 0 ? (
                      <div className="mgmt-card-trades mgmt-card-trades-flush">
                        {personTrades.map((t) => (
                          <span key={tradeId(t)} className="mgmt-trade-chip">{t.name || 'Trade'}</span>
                        ))}
                      </div>
                    ) : (
                      <div className="mgmt-card-empty-trades mgmt-card-trades-flush">No trades assigned</div>
                    )}
                  </>
                ) : (
                  <div className="mgmt-edit-panel">
                    <div className="ed-row">
                      <label>Name</label>
                      <input
                        className="ed-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="ed-row mgr-members-row">
                      <label>Trades</label>
                      <TradePicker trades={trades} selectedIds={editTrades} onChange={setEditTrades} />
                    </div>
                    <div className="mgmt-edit-actions">
                      <button className="cal-btn" onClick={cancelEditing} disabled={isSaving}>Cancel</button>
                      <button
                        className="cal-btn cal-btn-primary"
                        onClick={() => savePerson(p)}
                        disabled={!editName.trim() || isSaving}
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TradesTab = ({ trades, dispatch, onRequestConfirm }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingId, setSavingId] = useState(null);

  const submit = async () => {
    if (!name.trim()) return;
    await dispatch(createTrade({ name: name.trim() }));
    setName('');
    setShowAddForm(false);
  };

  const startEditing = (trade) => {
    setEditingId(trade._id);
    setEditName(trade.name || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveTrade = async (trade) => {
    const next = editName.trim();
    if (!next) return;
    if (next === trade.name) { cancelEditing(); return; }
    setSavingId(trade._id);
    try {
      await dispatch(updateTrade({ id: trade._id, updates: { name: next } }));
      cancelEditing();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="mgmt-section">
      <div className="mgmt-toolbar">
        <div className="mgmt-toolbar-meta">
          {(trades || []).length === 0
            ? 'No trades yet'
            : <><strong>{trades.length}</strong> {trades.length === 1 ? 'trade' : 'trades'}</>}
        </div>
        {!showAddForm && (
          <button className="cal-btn cal-btn-primary" onClick={() => setShowAddForm(true)}>
            <Icon name="plus" size={14} />
            <span>Add trade</span>
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="mgmt-add-card">
          <div className="mgmt-add-card-title">New trade</div>
          <div className="ed-row">
            <label>Trade name</label>
            <input
              className="ed-input"
              placeholder="e.g. Roofing"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              autoFocus
            />
          </div>
          <div className="mgmt-add-card-actions">
            <button className="cal-btn" onClick={() => { setName(''); setShowAddForm(false); }}>Cancel</button>
            <button className="cal-btn cal-btn-primary" onClick={submit} disabled={!name.trim()}>
              Add trade
            </button>
          </div>
        </div>
      )}

      {(trades || []).length === 0 ? (
        <div className="mgmt-empty">
          <div className="mgmt-empty-icon"><Icon name="tag" size={16} /></div>
          <div className="mgmt-empty-text">No trades yet</div>
          <div className="mgmt-empty-sub">Trades are skill categories (e.g. Roofing, Siding) you can assign to crews and users.</div>
        </div>
      ) : (
        <div className="mgmt-list">
          {(trades || []).map((t) => {
            const isEditing = editingId === t._id;
            const isSaving = savingId === t._id;
            return (
              <div key={t._id} className={`mgmt-card ${isEditing ? 'is-editing' : ''}`}>
                {!isEditing ? (
                  <div className="mgmt-card-row">
                    <span className="mgmt-card-name" style={{ paddingLeft: 0 }}>{t.name}</span>
                    <div className="mgmt-card-actions">
                      <button
                        className="mgmt-icon-btn"
                        onClick={() => startEditing(t)}
                        title="Rename trade"
                        aria-label="Rename trade"
                      >
                        <Icon name="edit" size={14} />
                      </button>
                      <button
                        className="mgmt-icon-btn is-danger"
                        onClick={() => onRequestConfirm({
                          title: 'Remove Trade',
                          message: `Remove "${t.name}"? Crews and users currently associated with this trade will have it unassigned.`,
                          confirmText: 'Remove',
                          type: 'danger',
                          onConfirm: () => dispatch(deleteTrade(t._id))
                        })}
                        title="Remove trade"
                        aria-label="Remove trade"
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mgmt-edit-panel">
                    <div className="ed-row">
                      <label>Trade name</label>
                      <input
                        className="ed-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveTrade(t); } }}
                        autoFocus
                      />
                    </div>
                    <div className="mgmt-edit-actions">
                      <button className="cal-btn" onClick={cancelEditing} disabled={isSaving}>Cancel</button>
                      <button
                        className="cal-btn cal-btn-primary"
                        onClick={() => saveTrade(t)}
                        disabled={!editName.trim() || isSaving}
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendarManagerDialog;
